import { useMemo, useState, type FormEvent } from 'react'
import {
  useRoles,
  useCreateRole,
  usePatchRole,
  useDeleteRole
} from '../hooks/useRoles'
import {
  ModalShell, Field, inputClass,
  CancelButton, PrimaryButton, DangerButton, ServerError
} from '../components/modal-form'
import { Icon } from '../components/Icon'
import { useMe } from '../hooks/useMe'
import {
  ROLE_TYPES,
  type LibraryRole,
  type RoleType,
  type LibraryRoleCreate,
  type LibraryRolePatch
} from '../types'

// AdminLibraryRoles — admin-only page for the org-level Roles catalogue
// (mig 039 / Roster rebuild PR R1). Mirrors AdminLibraryComponents /
// AdminLibraryActivities: grouped by role_type (staff / volunteer /
// contractor), click to edit, soft-delete preserves any EventRoles +
// RosterAssignments rows that reference the role.

export function AdminLibraryRoles() {
  const { data: me } = useMe()
  const { data: roles, isLoading, error } = useRoles()
  const [editing, setEditing] = useState<LibraryRole | 'new' | null>(null)

  // Group by role_type for visual scan; preserve ROLE_TYPES declared
  // order so headers always appear in the canonical sequence.
  const grouped = useMemo(() => {
    const buckets = new Map<RoleType, LibraryRole[]>()
    for (const r of roles ?? []) {
      const list = buckets.get(r.roleType) ?? []
      list.push(r)
      buckets.set(r.roleType, list)
    }
    return ROLE_TYPES
      .map((t) => ({ roleType: t, rows: buckets.get(t) ?? [] }))
      .filter((g) => g.rows.length > 0)
  }, [roles])

  if (me && !me.isAdmin) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-3xl mb-1">Roles library</h1>
          <p className="text-ink-soft">Admin only.</p>
        </div>
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          Editing the roles library requires admin permissions.
        </div>
      </div>
    )
  }

  const affectsCount = roles?.filter((r) => r.affectsServices).length ?? 0

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Roles library</h1>
          <p className="text-ink-soft">
            The catalogue of roles available to every event's roster. Configure
            which roles appear on a specific event from that event's Roster tab.
            Roles flagged “affects services” show up as crew chips on the
            Schedule tab.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 transition-colors"
        >
          + New role
        </button>
      </div>

      {isLoading && (
        <div className="text-ink-soft text-sm font-mono">Loading roles…</div>
      )}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load roles</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {roles && roles.length === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#dcebe5', color: '#0a4a3a' }}
          >
            <Icon name="users" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No roles yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Add a role here and it becomes available for every event to
            include on its roster. Mark a role “affects services” to have
            people in it appear as crew chips on the Schedule tab.
          </p>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
          >
            + Add the first role
          </button>
        </div>
      )}

      {roles && roles.length > 0 && (
        <div className="text-xs text-ink-soft font-mono">
          {roles.length} role{roles.length === 1 ? '' : 's'} · {affectsCount} affects services
        </div>
      )}

      {grouped.map(({ roleType, rows }) => (
        <section key={roleType} className="bg-white border border-line rounded overflow-hidden">
          <header className="px-4 py-2.5 border-b border-line bg-parchment/60 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold capitalize">{roleType}</h2>
            <span className="text-xs text-ink-soft font-mono">
              {rows.length} role{rows.length === 1 ? '' : 's'}
            </span>
          </header>
          <div className="divide-y divide-line">
            {rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setEditing(r)}
                className="w-full text-left px-4 py-3 flex items-start gap-4 hover:bg-parchment/40 transition-colors"
              >
                <Icon name="users" size={16} className="text-slate flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{r.name}</span>
                    <span className="text-[10px] text-ink-soft font-mono">{r.slug}</span>
                    {(r.defaultShiftStart || r.defaultShiftEnd) && (
                      <span className="text-[10px] text-ink-soft font-mono">
                        {r.defaultShiftStart ?? '?'}–{r.defaultShiftEnd ?? '?'}
                      </span>
                    )}
                  </div>
                  {r.description && (
                    <div className="text-xs text-ink-soft mt-0.5 line-clamp-1">
                      {r.description}
                    </div>
                  )}
                </div>
                {r.affectsServices && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded whitespace-nowrap"
                    style={{ background: '#dcebe5', color: '#0a4a3a' }}
                  >
                    affects services
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}

      {editing && (
        <RoleEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

// ---- Editor modal --------------------------------------------------------

function RoleEditor({
  initial, onClose
}: {
  initial: LibraryRole | null
  onClose: () => void
}) {
  const isEditing = initial != null
  const createMut = useCreateRole()
  const patchMut  = usePatchRole()
  const deleteMut = useDeleteRole()

  const [slug, setSlug]                         = useState(initial?.slug ?? '')
  const [name, setName]                         = useState(initial?.name ?? '')
  const [description, setDescription]           = useState(initial?.description ?? '')
  const [roleType, setRoleType]                 = useState<RoleType>(initial?.roleType ?? 'staff')
  const [affectsServices, setAffectsServices]   = useState(initial?.affectsServices ?? false)
  const [defaultShiftStart, setDefaultShiftStart] = useState(initial?.defaultShiftStart ?? '')
  const [defaultShiftEnd,   setDefaultShiftEnd]   = useState(initial?.defaultShiftEnd ?? '')
  const [sortOrder, setSortOrder]               = useState<number>(initial?.sortOrder ?? 0)

  // Auto-suggest slug from name on the CREATE path only.
  function onNameChange(v: string) {
    setName(v)
    if (!isEditing && (slug === '' || slug === slugify(name))) {
      setSlug(slugify(v))
    }
  }

  const trimmedSlug = slug.trim()
  const trimmedName = name.trim()
  const fieldErrors: { slug?: string; name?: string; shiftStart?: string; shiftEnd?: string } = {}
  if (!isEditing) {
    if (trimmedSlug.length === 0) fieldErrors.slug = 'Slug is required'
    else if (trimmedSlug.length > 80) fieldErrors.slug = 'Slug must be 80 chars or fewer'
    else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmedSlug)) {
      fieldErrors.slug = 'Slug must be kebab-case (lowercase letters, digits, hyphens)'
    }
  }
  if (trimmedName.length === 0) fieldErrors.name = 'Name is required'
  else if (trimmedName.length > 120) fieldErrors.name = 'Name must be 120 chars or fewer'
  const hhmm = /^[0-2][0-9]:[0-5][0-9]$/
  if (defaultShiftStart && !hhmm.test(defaultShiftStart)) fieldErrors.shiftStart = 'Use HH:mm (24h)'
  if (defaultShiftEnd   && !hhmm.test(defaultShiftEnd))   fieldErrors.shiftEnd   = 'Use HH:mm (24h)'

  const isValid = Object.keys(fieldErrors).length === 0
  const saving = createMut.isPending || patchMut.isPending
  const deleting = deleteMut.isPending
  const serverError = (createMut.error ?? patchMut.error ?? deleteMut.error) as Error | null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const startVal = defaultShiftStart.trim() === '' ? null : defaultShiftStart.trim()
    const endVal   = defaultShiftEnd.trim()   === '' ? null : defaultShiftEnd.trim()
    const descVal  = description.trim() === '' ? null : description.trim()
    if (isEditing && initial) {
      const patch: LibraryRolePatch = {
        name: trimmedName,
        description: descVal,
        defaultShiftStart: startVal,
        defaultShiftEnd:   endVal,
        roleType,
        affectsServices,
        sortOrder
      }
      patchMut.mutate({ id: initial.id, body: patch }, { onSuccess: () => onClose() })
    } else {
      const body: LibraryRoleCreate = {
        slug: trimmedSlug,
        name: trimmedName,
        description: descVal,
        defaultShiftStart: startVal,
        defaultShiftEnd:   endVal,
        roleType,
        affectsServices,
        sortOrder
      }
      createMut.mutate(body, { onSuccess: () => onClose() })
    }
  }

  function handleDelete() {
    if (!initial) return
    const ok = window.confirm(
      `Remove "${initial.name}" from the catalogue? Events that currently include this role keep working — only future Configure-Roles lookups stop seeing it.`
    )
    if (!ok) return
    deleteMut.mutate(initial.id, { onSuccess: () => onClose() })
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit role' : 'New role'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="lg"
      footer={
        <>
          {isEditing && (
            <DangerButton onClick={handleDelete} disabled={saving || deleting}>
              {deleting ? 'Removing…' : 'Remove'}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add role'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Name" error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              maxLength={120}
              autoFocus
              required
              className={inputClass(!!fieldErrors.name)}
              placeholder="e.g. Welcome Host"
            />
          </Field>

          <Field
            label="Slug"
            error={fieldErrors.slug}
            hint={isEditing ? 'Locked — slugs are the public contract' : 'Auto-suggested. Kebab-case.'}
          >
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              maxLength={80}
              required={!isEditing}
              disabled={isEditing}
              className={inputClass(!!fieldErrors.slug) + (isEditing ? ' opacity-60 cursor-not-allowed' : '')}
              placeholder="welcome-host"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select
                value={roleType}
                onChange={(e) => setRoleType(e.target.value as RoleType)}
                className={inputClass(false)}
              >
                {ROLE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Sort order" hint="Lower = higher in the grid">
              <input
                type="number"
                min={0}
                step={10}
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                className={inputClass(false)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Default shift start" hint="HH:mm — optional" error={fieldErrors.shiftStart}>
              <input
                type="text"
                value={defaultShiftStart}
                onChange={(e) => setDefaultShiftStart(e.target.value)}
                maxLength={5}
                placeholder="09:00"
                className={inputClass(!!fieldErrors.shiftStart)}
              />
            </Field>
            <Field label="Default shift end" hint="HH:mm — optional" error={fieldErrors.shiftEnd}>
              <input
                type="text"
                value={defaultShiftEnd}
                onChange={(e) => setDefaultShiftEnd(e.target.value)}
                maxLength={5}
                placeholder="15:00"
                className={inputClass(!!fieldErrors.shiftEnd)}
              />
            </Field>
          </div>

          <Field label="Description (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={inputClass(false)}
              placeholder="Short description shown to admins on the Configure Roles picker."
            />
          </Field>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={affectsServices}
              onChange={(e) => setAffectsServices(e.target.checked)}
              disabled={saving}
              className="w-4 h-4 mt-0.5 accent-green cursor-pointer disabled:cursor-not-allowed"
            />
            <div>
              <div className="text-sm font-semibold">Affects services</div>
              <div className="text-xs text-ink-soft">
                People in this role appear as crew chips on the Schedule tab.
                Use for operational duty-coordinator roles only (Event Manager,
                Duty Manager, Controller / MOM, Station Foreman).
              </div>
            </div>
          </label>

          {serverError && <ServerError title="Could not save" error={serverError} />}
        </div>
      }
    />
  )
}

// kebab-case from a free-text name.
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
